import * as i1 from '@angular/common';
import { NgStyle, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { ViewEncapsulation, CUSTOM_ELEMENTS_SCHEMA, Component, Input, EventEmitter, Output, ViewChild } from '@angular/core';
import 'hls-video-element';
import 'media-chrome';
import 'media-chrome/media-theme-element';
import 'media-chrome/menu';

const EMBED_BASE = 'https://iframe.videodelivery.net';
const MATCH_SRC = /(?:cloudflarestream\.com|videodelivery\.net)\/([\w-.]+)/i;
const API_URL = 'https://embed.videodelivery.net/embed/sdk.latest.js';
const API_GLOBAL = 'Stream';
const VideoEvents = [
    'abort',
    'canplay',
    'canplaythrough',
    'complete',
    'durationchange',
    'emptied',
    'encrypted',
    'ended',
    'error',
    'loadeddata',
    'loadedmetadata',
    'loadstart',
    'pause',
    'play',
    'playing',
    'progress',
    'ratechange',
    'seeked',
    'seeking',
    'stalled',
    'suspend',
    'timeupdate',
    'volumechange',
    'waiting',
    'waitingforkey',
    'resize',
    'enterpictureinpicture',
    'leavepictureinpicture',
];

function boolToBinary(props) {
    let p = {};
    for (let key in props) {
        let val = props[key];
        if (val === true || val === '')
            p[key] = 1;
        else if (val === false)
            p[key] = 0;
        else if (val != null)
            p[key] = val;
    }
    return p;
}
function namedNodeMapToObject(namedNodeMap) {
    let obj = {};
    for (let attr of namedNodeMap) {
        obj[attr.name] = attr.value;
    }
    return obj;
}
function serializeAttributes(attrs) {
    let html = '';
    for (const key in attrs) {
        const value = attrs[key];
        if (value === '')
            html += ` ${key}`;
        else
            html += ` ${key}="${value}"`;
    }
    return html;
}
function serialize(props) {
    return String(new URLSearchParams(boolToBinary(props)));
}
function getTemplateHTML(attrs) {
    const iframeAttrs = {
        src: serializeIframeUrl(attrs),
        width: '100%',
        height: '100%',
        allow: 'accelerometer; fullscreen; autoplay; encrypted-media; gyroscope; picture-in-picture',
        style: 'border-radius: 5px; border: 0px;',
    };
    return /*html*/ `
      <style>
        :host {
          display: inline-block;
          min-width: 300px;
          min-height: 150px;
          position: relative;
        }
        iframe {
          position: absolute;
          top: 0;
          left: 0;
        }
        :host(:not([controls])) {
          pointer-events: none;
        }
      </style>
      <iframe${serializeAttributes(iframeAttrs)} id="player-iframe" ></iframe>
    `;
}
function serializeIframeUrl(attrs) {
    if (!attrs.src)
        return;
    const matches = attrs.src.match(MATCH_SRC);
    const srcId = matches && matches[1];
    const params = {
        // ?controls=true is enabled by default in the iframe
        controls: attrs.controls === '' ? null : 0,
        autoplay: attrs.autoplay,
        loop: attrs.loop,
        muted: attrs.muted,
        preload: attrs.preload,
        poster: attrs.poster,
        defaultTextTrack: attrs.defaulttexttrack,
        primaryColor: attrs.primarycolor,
        letterboxColor: attrs.letterboxcolor,
        startTime: attrs.starttime,
        'ad-url': attrs.adurl,
    };
    return `${EMBED_BASE}/${srcId}?${serialize(params)}`;
}

/**
 * A utility to create Promises with convenient public resolve and reject methods.
 * @return {Promise}
 */
class PublicPromise extends Promise {
    constructor(executor = () => { }) {
        let res;
        let rej;
        super((resolve, reject) => {
            res = resolve;
            rej = reject;
            executor(resolve, reject);
        });
        this.resolve = res;
        this.reject = rej;
    }
}
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// https://developers.cloudflare.com/stream/viewing-videos/using-the-stream-player/using-the-player-api/
//@ts-nocheck
class CloudflareVideoElement extends (globalThis.HTMLElement ?? class {
}) {
    constructor() {
        super(...arguments);
        this.loadComplete = new PublicPromise();
        this.#readyState = 0;
    }
    static { this.getTemplateHTML = getTemplateHTML; }
    static { this.shadowRootOptions = { mode: 'open' }; }
    static { this.observedAttributes = ['autoplay', 'controls', 'crossorigin', 'loop', 'muted', 'playsinline', 'poster', 'preload', 'src']; }
    #loadRequested;
    #hasLoaded;
    #isInit;
    #readyState;
    async load() {
        if (this.#loadRequested)
            return;
        if (this.#hasLoaded)
            this.loadComplete = new PublicPromise();
        this.#hasLoaded = true;
        // Wait 1 tick to allow other attributes to be set.
        await (this.#loadRequested = Promise.resolve());
        this.#loadRequested = null;
        this.#readyState = 0;
        this.dispatchEvent(new Event('emptied'));
        let oldApi = this.api;
        this.api = null;
        if (!this.src) {
            return;
        }
        const matches = this.src.match(MATCH_SRC);
        const srcId = matches && matches[1];
        if (this.#isInit) {
            this.api = oldApi;
            this.api.src = srcId;
        }
        else {
            this.#isInit = true;
            let serverRendered = this.shadowRoot;
            if (!this.shadowRoot) {
                this.attachShadow({ mode: 'open' });
                this.shadowRoot.innerHTML = getTemplateHTML(namedNodeMapToObject(this.attributes));
            }
            const iframe = this.shadowRoot.querySelector('iframe');
            const Stream = await loadScript(API_URL, API_GLOBAL);
            if (serverRendered) {
                // The Cloudflare Player API has a bug where it doesn't work with a SSR iframe
                // because it loads too quickly and the `iframeReady` post message is lost.
                // To work around this, we need to reload the iframe.
                iframe.src = `${iframe.src}`;
            }
            this.api = Stream(iframe);
            this.api.addEventListener('loadedmetadata', () => {
                this.#readyState = 1; // HTMLMediaElement.HAVE_METADATA
            });
            this.api.addEventListener('loadeddata', () => {
                this.#readyState = 2; // HTMLMediaElement.HAVE_CURRENT_DATA
            });
            this.api.addEventListener('playing', () => {
                this.#readyState = 3; // HTMLMediaElement.HAVE_FUTURE_DATA
            });
            // The video events are dispatched on the api instance.
            // This makes it possible to add event listeners before the element is upgraded.
            for (let type of VideoEvents) {
                this.api.addEventListener(type, (evt) => {
                    this.dispatchEvent(new Event(evt.type));
                });
            }
        }
        Promise.resolve().then(() => {
            this.dispatchEvent(new Event('loadcomplete'));
            this.loadComplete.resolve();
        });
        await this.loadComplete;
    }
    async attributeChangedCallback(attrName, oldValue, newValue) {
        if (oldValue === newValue)
            return;
        // This is required to come before the await for resolving loadComplete.
        switch (attrName) {
            case 'src': {
                this.load();
                return;
            }
        }
        await this.loadComplete;
        switch (attrName) {
            case 'autoplay':
            case 'controls':
            case 'loop': {
                this.api[attrName] = this.hasAttribute(attrName);
                break;
            }
            case 'poster':
            case 'preload': {
                this.api[attrName] = this.getAttribute(attrName);
                break;
            }
        }
    }
    async play() {
        await this.loadComplete;
        return this.api?.play();
    }
    async pause() {
        await this.loadComplete;
        return this.api?.pause();
    }
    get ended() {
        return this.api?.ended ?? false;
    }
    get seeking() {
        return this.api?.seeking;
    }
    get readyState() {
        return this.#readyState;
    }
    get videoWidth() {
        return this.api?.videoWidth;
    }
    get videoHeight() {
        return this.api?.videoHeight;
    }
    get preload() {
        return this.getAttribute('preload');
    }
    set preload(val) {
        if (this.preload == val)
            return;
        this.setAttribute('preload', `${val}`);
    }
    get src() {
        return this.getAttribute('src');
    }
    set src(val) {
        if (this.src == val)
            return;
        this.setAttribute('src', `${val}`);
    }
    get paused() {
        return this.api?.paused ?? true;
    }
    get duration() {
        if (this.api?.duration > 0) {
            return this.api?.duration;
        }
        return NaN;
    }
    get autoplay() {
        return this.hasAttribute('autoplay');
    }
    set autoplay(val) {
        if (this.autoplay == val)
            return;
        this.toggleAttribute('autoplay', Boolean(val));
    }
    get buffered() {
        return this.api?.buffered;
    }
    get played() {
        return this.api?.played;
    }
    get controls() {
        return this.hasAttribute('controls');
    }
    set controls(val) {
        if (this.controls == val)
            return;
        this.toggleAttribute('controls', Boolean(val));
    }
    get currentTime() {
        return this.api?.currentTime ?? 0;
    }
    set currentTime(val) {
        if (this.currentTime == val)
            return;
        this.loadComplete.then(() => {
            this.api.currentTime = val;
        });
    }
    get defaultMuted() {
        return this.hasAttribute('muted');
    }
    set defaultMuted(val) {
        if (this.defaultMuted == val)
            return;
        this.toggleAttribute('muted', Boolean(val));
    }
    get loop() {
        return this.hasAttribute('loop');
    }
    set loop(val) {
        if (this.loop == val)
            return;
        this.toggleAttribute('loop', Boolean(val));
    }
    get muted() {
        return this.api?.muted ?? this.defaultMuted;
    }
    set muted(val) {
        if (this.muted == val)
            return;
        this.loadComplete.then(() => {
            this.api.muted = val;
        });
    }
    get playbackRate() {
        return this.api?.playbackRate ?? 1;
    }
    set playbackRate(val) {
        if (this.playbackRate == val)
            return;
        this.loadComplete.then(() => {
            this.api.playbackRate = val;
        });
    }
    get playsInline() {
        return this.hasAttribute('playsinline');
    }
    set playsInline(val) {
        if (this.playsInline == val)
            return;
        this.toggleAttribute('playsinline', Boolean(val));
    }
    get poster() {
        return this.getAttribute('poster');
    }
    set poster(val) {
        if (this.poster == val)
            return;
        this.setAttribute('poster', `${val}`);
    }
    get volume() {
        return this.api?.volume ?? 1;
    }
    set volume(val) {
        if (this.volume == val)
            return;
        this.loadComplete.then(() => {
            this.api.volume = val;
        });
    }
}
const loadScriptCache = {};
async function loadScript(src, globalName) {
    if (loadScriptCache[src])
        return loadScriptCache[src];
    if (globalName && self[globalName]) {
        await delay(0);
        return self[globalName];
    }
    return (loadScriptCache[src] = new Promise(function (resolve, reject) {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(self[globalName]);
        script.onerror = reject;
        document.head.append(script);
    }));
}
if (globalThis.customElements && !globalThis.customElements.get('cloudflare-video')) {
    globalThis.customElements.define('cloudflare-video', CloudflareVideoElement);
}

class PlayerVideoLoaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerVideoLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerVideoLoaderComponent, isStandalone: true, selector: "player-video-loader", ngImport: i0, template: `<div class="lds-ring">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>`, isInline: true, styles: [".lds-ring{color:#fff}.lds-ring,.lds-ring div{box-sizing:border-box}.lds-ring{display:inline-block;position:relative;width:80px;height:80px}.lds-ring div{box-sizing:border-box;display:block;position:absolute;width:64px;height:64px;margin:8px;border:8px solid currentColor;border-radius:50%;animation:lds-ring 1.2s cubic-bezier(.5,0,.5,1) infinite;border-color:currentColor transparent transparent transparent}.lds-ring div:nth-child(1){animation-delay:-.45s}.lds-ring div:nth-child(2){animation-delay:-.3s}.lds-ring div:nth-child(3){animation-delay:-.15s}@keyframes lds-ring{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerVideoLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-video-loader', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<div class="lds-ring">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>`, styles: [".lds-ring{color:#fff}.lds-ring,.lds-ring div{box-sizing:border-box}.lds-ring{display:inline-block;position:relative;width:80px;height:80px}.lds-ring div{box-sizing:border-box;display:block;position:absolute;width:64px;height:64px;margin:8px;border:8px solid currentColor;border-radius:50%;animation:lds-ring 1.2s cubic-bezier(.5,0,.5,1) infinite;border-color:currentColor transparent transparent transparent}.lds-ring div:nth-child(1){animation-delay:-.45s}.lds-ring div:nth-child(2){animation-delay:-.3s}.lds-ring div:nth-child(3){animation-delay:-.15s}@keyframes lds-ring{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }] });

class PlayerCaptionButtonComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerCaptionButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerCaptionButtonComponent, isStandalone: true, selector: "player-caption-button", ngImport: i0, template: `<media-captions-button class="e11-video-control-button e11-relative">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#cc-icon"></use>
      <path
        id="cc-icon"
        d="M9 13.4124C9 12.0801 10.0801 11 11.4124 11H24.5876C25.9199 11 27 12.0801 27 13.4124V22.5876C27 23.9199 25.9199 25 24.5876 25H11.4124C10.0801 25 9 23.9199 9 22.5876V13.4124ZM12 16.1134C12 15.4985 12.4985 15 13.1134 15H15.8866C16.5015 15 17 15.4985 17 16.1134V19.8866C17 20.5015 16.5015 21 15.8866 21H13.1134C12.4985 21 12 20.5015 12 19.8866V16.1134ZM13.5517 16.4545H15.4483V19.5455H13.5517V16.4545ZM17 17H15.4483V19H17V17ZM20.1134 15C19.4985 15 19 15.4985 19 16.1134V19.8866C19 20.5015 19.4985 21 20.1134 21H22.8866C23.5015 21 24 20.5015 24 19.8866V16.1134C24 15.4985 23.5015 15 22.8866 15H20.1134ZM22.4483 16.4545H20.5517V19.5455H22.4483V16.4545ZM22.4483 17H24V19H22.4483V17Z"
      />
    </svg>
  </media-captions-button>`, isInline: true, styles: ["media-captions-button:not([mediasubtitleslist]) svg{opacity:.3}media-captions-button[mediasubtitleslist]:after{content:\"\";display:block;position:absolute;width:0;height:3px;border-radius:3px;background-color:red;bottom:19%;left:50%;transition:all .1s cubic-bezier(0,0,.2,1),width .1s cubic-bezier(0,0,.2,1)}media-captions-button[mediasubtitleslist][aria-checked=true]:after{left:25%;width:50%;transition:left .25s cubic-bezier(0,0,.2,1),width .25s cubic-bezier(0,0,.2,1)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerCaptionButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-caption-button', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<media-captions-button class="e11-video-control-button e11-relative">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#cc-icon"></use>
      <path
        id="cc-icon"
        d="M9 13.4124C9 12.0801 10.0801 11 11.4124 11H24.5876C25.9199 11 27 12.0801 27 13.4124V22.5876C27 23.9199 25.9199 25 24.5876 25H11.4124C10.0801 25 9 23.9199 9 22.5876V13.4124ZM12 16.1134C12 15.4985 12.4985 15 13.1134 15H15.8866C16.5015 15 17 15.4985 17 16.1134V19.8866C17 20.5015 16.5015 21 15.8866 21H13.1134C12.4985 21 12 20.5015 12 19.8866V16.1134ZM13.5517 16.4545H15.4483V19.5455H13.5517V16.4545ZM17 17H15.4483V19H17V17ZM20.1134 15C19.4985 15 19 15.4985 19 16.1134V19.8866C19 20.5015 19.4985 21 20.1134 21H22.8866C23.5015 21 24 20.5015 24 19.8866V16.1134C24 15.4985 23.5015 15 22.8866 15H20.1134ZM22.4483 16.4545H20.5517V19.5455H22.4483V16.4545ZM22.4483 17H24V19H22.4483V17Z"
      />
    </svg>
  </media-captions-button>`, styles: ["media-captions-button:not([mediasubtitleslist]) svg{opacity:.3}media-captions-button[mediasubtitleslist]:after{content:\"\";display:block;position:absolute;width:0;height:3px;border-radius:3px;background-color:red;bottom:19%;left:50%;transition:all .1s cubic-bezier(0,0,.2,1),width .1s cubic-bezier(0,0,.2,1)}media-captions-button[mediasubtitleslist][aria-checked=true]:after{left:25%;width:50%;transition:left .25s cubic-bezier(0,0,.2,1),width .25s cubic-bezier(0,0,.2,1)}\n"] }]
        }] });

class PlayerFullScreenButtonComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerFullScreenButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerFullScreenButtonComponent, isStandalone: true, selector: "player-full-screen-button", ngImport: i0, template: `<media-fullscreen-button class="e11-video-control-button">
    <svg slot="enter" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#fs-enter-paths"></use>
      <g id="fs-enter-paths">
        <path class="ulbounce" d="M11 15H9V9H15V11H11V15Z" />
        <path class="urbounce" d="M21 11L21 9L27 9L27 15L25 15L25 11L21 11Z" />
        <path class="dlbounce" d="M15 25L15 27L9 27L9 21L11 21L11 25L15 25Z" />
        <path class="drbounce" d="M25 21L27 21L27 27L21 27L21 25L25 25L25 21Z" />
      </g>
    </svg>
    <svg slot="exit" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#fs-exit-paths"></use>
      <g id="fs-exit-paths">
        <path class="drbounce" d="M14 9L16 9L16 16L9 16L9 14L14 14L14 9Z" />
        <path class="dlbounce" d="M27 14L27 16L20 16L20 9L22 9L22 14L27 14Z" />
        <path class="urbounce" d="M9 22L9 20L16 20L16 27L14 27L14 22L9 22Z" />
        <path class="ulbounce" d="M22 27H20V20H27V22H22V27Z" />
      </g>
    </svg>
  </media-fullscreen-button>`, isInline: true, styles: ["media-fullscreen-button path{translate:0% 0%}media-fullscreen-button:hover path{animation:.35s up-left-bounce cubic-bezier(.34,1.56,.64,1)}media-fullscreen-button:hover .urbounce{animation-name:up-right-bounce}media-fullscreen-button:hover .dlbounce{animation-name:down-left-bounce}media-fullscreen-button:hover .drbounce{animation-name:down-right-bounce}@keyframes up-left-bounce{0%{translate:0 0}50%{translate:-4% -4%}}@keyframes up-right-bounce{0%{translate:0 0}50%{translate:4% -4%}}@keyframes down-left-bounce{0%{translate:0 0}50%{translate:-4% 4%}}@keyframes down-right-bounce{0%{translate:0 0}50%{translate:4% 4%}}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerFullScreenButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-full-screen-button', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<media-fullscreen-button class="e11-video-control-button">
    <svg slot="enter" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#fs-enter-paths"></use>
      <g id="fs-enter-paths">
        <path class="ulbounce" d="M11 15H9V9H15V11H11V15Z" />
        <path class="urbounce" d="M21 11L21 9L27 9L27 15L25 15L25 11L21 11Z" />
        <path class="dlbounce" d="M15 25L15 27L9 27L9 21L11 21L11 25L15 25Z" />
        <path class="drbounce" d="M25 21L27 21L27 27L21 27L21 25L25 25L25 21Z" />
      </g>
    </svg>
    <svg slot="exit" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#fs-exit-paths"></use>
      <g id="fs-exit-paths">
        <path class="drbounce" d="M14 9L16 9L16 16L9 16L9 14L14 14L14 9Z" />
        <path class="dlbounce" d="M27 14L27 16L20 16L20 9L22 9L22 14L27 14Z" />
        <path class="urbounce" d="M9 22L9 20L16 20L16 27L14 27L14 22L9 22Z" />
        <path class="ulbounce" d="M22 27H20V20H27V22H22V27Z" />
      </g>
    </svg>
  </media-fullscreen-button>`, styles: ["media-fullscreen-button path{translate:0% 0%}media-fullscreen-button:hover path{animation:.35s up-left-bounce cubic-bezier(.34,1.56,.64,1)}media-fullscreen-button:hover .urbounce{animation-name:up-right-bounce}media-fullscreen-button:hover .dlbounce{animation-name:down-left-bounce}media-fullscreen-button:hover .drbounce{animation-name:down-right-bounce}@keyframes up-left-bounce{0%{translate:0 0}50%{translate:-4% -4%}}@keyframes up-right-bounce{0%{translate:0 0}50%{translate:4% -4%}}@keyframes down-left-bounce{0%{translate:0 0}50%{translate:-4% 4%}}@keyframes down-right-bounce{0%{translate:0 0}50%{translate:4% 4%}}\n"] }]
        }] });

class PlayerMiniPlayerButtonComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerMiniPlayerButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerMiniPlayerButtonComponent, isStandalone: true, selector: "player-mini-player-button", ngImport: i0, template: `<media-pip-button class="e11-video-control-button">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#pip-icon"></use>
      <path d="M25 17H17V23H25V17Z" />
      <path
        id="pip-icon"
        d="M7 11C7 9.89543 7.89545 9 9 9H27.0161C28.1207 9 29.0161 9.89543 29.0161 11V24.8837C29.0161 25.9883 28.1207 26.8837 27.0162 26.8837H9C7.89545 26.8837 7 25.9883 7 24.8837V11ZM9 11H27V25H9V11Z"
      />
    </svg>
  </media-pip-button>`, isInline: true, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerMiniPlayerButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'player-mini-player-button',
                    standalone: true,
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                    encapsulation: ViewEncapsulation.None,
                    template: `<media-pip-button class="e11-video-control-button">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#pip-icon"></use>
      <path d="M25 17H17V23H25V17Z" />
      <path
        id="pip-icon"
        d="M7 11C7 9.89543 7.89545 9 9 9H27.0161C28.1207 9 29.0161 9.89543 29.0161 11V24.8837C29.0161 25.9883 28.1207 26.8837 27.0162 26.8837H9C7.89545 26.8837 7 25.9883 7 24.8837V11ZM9 11H27V25H9V11Z"
      />
    </svg>
  </media-pip-button>`,
                }]
        }] });

class PlayerPlayButtonComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerPlayButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerPlayButtonComponent, isStandalone: true, selector: "player-play-button", ngImport: i0, template: ` <media-play-button mediapaused class="e11-video-control-button" style="">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#icon-play"></use>
      <g id="icon-play">
        <path id="play-p1" d="M18.5 14L12 10V26L18.5 22V14Z" />
        <path id="play-p2" d="M18 13.6953L25 18L18 22.3086V13.6953Z" />
        <path id="pause-p1" d="M16 10H12V26H16V10Z" />
        <path id="pause-p2" d="M21 10H25V26H21V10Z" />
      </g>
    </svg>
  </media-play-button>`, isInline: true, styles: ["media-play-button{--media-button-icon-width: 30px;padding:6px 10px}media-play-button :is(#play-p1,#play-p2,#pause-p1,#pause-p2){transition:clip-path .25s ease-in}media-play-button:not([mediapaused]) #play-p2{transition:clip-path .35s ease-in}media-play-button :is(#pause-p1,#pause-p2),media-play-button[mediapaused] :is(#play-p1,#play-p2){clip-path:inset(0)}media-play-button #play-p1{clip-path:inset(0 100% 0 0)}media-play-button #play-p2{clip-path:inset(0 20% 0 100%)}media-play-button[mediapaused] #pause-p1{clip-path:inset(50% 0 50% 0)}media-play-button[mediapaused] #pause-p2{clip-path:inset(50% 0 50% 0)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerPlayButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-play-button', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: ` <media-play-button mediapaused class="e11-video-control-button" style="">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#icon-play"></use>
      <g id="icon-play">
        <path id="play-p1" d="M18.5 14L12 10V26L18.5 22V14Z" />
        <path id="play-p2" d="M18 13.6953L25 18L18 22.3086V13.6953Z" />
        <path id="pause-p1" d="M16 10H12V26H16V10Z" />
        <path id="pause-p2" d="M21 10H25V26H21V10Z" />
      </g>
    </svg>
  </media-play-button>`, styles: ["media-play-button{--media-button-icon-width: 30px;padding:6px 10px}media-play-button :is(#play-p1,#play-p2,#pause-p1,#pause-p2){transition:clip-path .25s ease-in}media-play-button:not([mediapaused]) #play-p2{transition:clip-path .35s ease-in}media-play-button :is(#pause-p1,#pause-p2),media-play-button[mediapaused] :is(#play-p1,#play-p2){clip-path:inset(0)}media-play-button #play-p1{clip-path:inset(0 100% 0 0)}media-play-button #play-p2{clip-path:inset(0 20% 0 100%)}media-play-button[mediapaused] #pause-p1{clip-path:inset(50% 0 50% 0)}media-play-button[mediapaused] #pause-p2{clip-path:inset(50% 0 50% 0)}\n"] }]
        }], ctorParameters: () => [] });

class PlayerSettingsMenuButtonComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerSettingsMenuButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerSettingsMenuButtonComponent, isStandalone: true, selector: "player-settings-menu-button", ngImport: i0, template: `<media-settings-menu-button class="e11-video-control-button">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#settings-icon"></use>
      <path
        id="settings-icon"
        d="M11.8153 12.0477L14.2235 12.9602C14.6231 12.6567 15.0599 12.3996 15.5258 12.1971L15.9379 9.66561C16.5985 9.50273 17.2891 9.41632 18 9.41632C18.7109 9.41632 19.4016 9.50275 20.0622 9.66566L20.4676 12.1555C20.9584 12.3591 21.418 12.6227 21.8372 12.9372L24.1846 12.0477C25.1391 13.0392 25.8574 14.2597 26.249 15.6186L24.3196 17.1948C24.3531 17.4585 24.3704 17.7272 24.3704 18C24.3704 18.2727 24.3531 18.5415 24.3196 18.8051L26.249 20.3814C25.8574 21.7403 25.1391 22.9607 24.1846 23.9522L21.8372 23.0628C21.4179 23.3772 20.9584 23.6408 20.4676 23.8445L20.0622 26.3343C19.4016 26.4972 18.7109 26.5836 18 26.5836C17.2891 26.5836 16.5985 26.4972 15.9379 26.3344L15.5258 23.8029C15.0599 23.6003 14.6231 23.3433 14.2236 23.0398L11.8154 23.9523C10.8609 22.9608 10.1426 21.7404 9.75098 20.3815L11.7633 18.7375C11.7352 18.4955 11.7208 18.2495 11.7208 18C11.7208 17.7505 11.7352 17.5044 11.7633 17.2625L9.75098 15.6185C10.1426 14.2596 10.8609 13.0392 11.8153 12.0477ZM18 20.75C19.5188 20.75 20.75 19.5188 20.75 18C20.75 16.4812 19.5188 15.25 18 15.25C16.4812 15.25 15.25 16.4812 15.25 18C15.25 19.5188 16.4812 20.75 18 20.75Z"
      />
    </svg>
  </media-settings-menu-button>`, isInline: true, styles: ["media-settings-menu{position:absolute;border-radius:12px;right:12px;bottom:61px;z-index:70;will-change:width,height;text-shadow:0 0 2px rgba(0,0,0,.5);transition:opacity .1s cubic-bezier(0,0,.2,1);-webkit-user-select:none;user-select:none;--media-settings-menu-min-width: 220px}[mediaisfullscreen] media-settings-menu{--media-settings-menu-min-width: 320px;right:24px;bottom:70px}media-settings-menu-item{height:40px;font-size:13px;font-weight:500;padding-top:0;padding-bottom:0}[mediaisfullscreen] media-settings-menu-item{font-size:20px;height:50px}media-settings-menu-item[submenusize=\"0\"]{display:none}.quality-settings[submenusize=\"1\"]{display:none}media-settings-menu-button svg{transition:transform .1s cubic-bezier(.4,0,1,1);transform:rotate(0)}media-settings-menu-button[aria-expanded=true] svg{transform:rotate(30deg)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerSettingsMenuButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-settings-menu-button', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<media-settings-menu-button class="e11-video-control-button">
    <svg slot="icon" viewBox="0 0 36 36">
      <use class="svg-shadow" xlink:href="#settings-icon"></use>
      <path
        id="settings-icon"
        d="M11.8153 12.0477L14.2235 12.9602C14.6231 12.6567 15.0599 12.3996 15.5258 12.1971L15.9379 9.66561C16.5985 9.50273 17.2891 9.41632 18 9.41632C18.7109 9.41632 19.4016 9.50275 20.0622 9.66566L20.4676 12.1555C20.9584 12.3591 21.418 12.6227 21.8372 12.9372L24.1846 12.0477C25.1391 13.0392 25.8574 14.2597 26.249 15.6186L24.3196 17.1948C24.3531 17.4585 24.3704 17.7272 24.3704 18C24.3704 18.2727 24.3531 18.5415 24.3196 18.8051L26.249 20.3814C25.8574 21.7403 25.1391 22.9607 24.1846 23.9522L21.8372 23.0628C21.4179 23.3772 20.9584 23.6408 20.4676 23.8445L20.0622 26.3343C19.4016 26.4972 18.7109 26.5836 18 26.5836C17.2891 26.5836 16.5985 26.4972 15.9379 26.3344L15.5258 23.8029C15.0599 23.6003 14.6231 23.3433 14.2236 23.0398L11.8154 23.9523C10.8609 22.9608 10.1426 21.7404 9.75098 20.3815L11.7633 18.7375C11.7352 18.4955 11.7208 18.2495 11.7208 18C11.7208 17.7505 11.7352 17.5044 11.7633 17.2625L9.75098 15.6185C10.1426 14.2596 10.8609 13.0392 11.8153 12.0477ZM18 20.75C19.5188 20.75 20.75 19.5188 20.75 18C20.75 16.4812 19.5188 15.25 18 15.25C16.4812 15.25 15.25 16.4812 15.25 18C15.25 19.5188 16.4812 20.75 18 20.75Z"
      />
    </svg>
  </media-settings-menu-button>`, styles: ["media-settings-menu{position:absolute;border-radius:12px;right:12px;bottom:61px;z-index:70;will-change:width,height;text-shadow:0 0 2px rgba(0,0,0,.5);transition:opacity .1s cubic-bezier(0,0,.2,1);-webkit-user-select:none;user-select:none;--media-settings-menu-min-width: 220px}[mediaisfullscreen] media-settings-menu{--media-settings-menu-min-width: 320px;right:24px;bottom:70px}media-settings-menu-item{height:40px;font-size:13px;font-weight:500;padding-top:0;padding-bottom:0}[mediaisfullscreen] media-settings-menu-item{font-size:20px;height:50px}media-settings-menu-item[submenusize=\"0\"]{display:none}.quality-settings[submenusize=\"1\"]{display:none}media-settings-menu-button svg{transition:transform .1s cubic-bezier(.4,0,1,1);transform:rotate(0)}media-settings-menu-button[aria-expanded=true] svg{transform:rotate(30deg)}\n"] }]
        }], ctorParameters: () => [] });

class PlayerTimeDisplayComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerTimeDisplayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerTimeDisplayComponent, isStandalone: true, selector: "player-time-display", host: { classAttribute: "e11-flex" }, ngImport: i0, template: ` <media-time-display showduration></media-time-display>`, isInline: true, styles: ["media-time-display{padding-top:6px;padding-bottom:6px;font-size:13px}[mediaisfullscreen] media-time-display{font-size:20px}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerTimeDisplayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-time-display', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: ` <media-time-display showduration></media-time-display>`, host: {
                        class: 'e11-flex',
                    }, styles: ["media-time-display{padding-top:6px;padding-bottom:6px;font-size:13px}[mediaisfullscreen] media-time-display{font-size:20px}\n"] }]
        }] });

class PlayerVolumeControlComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerVolumeControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerVolumeControlComponent, isStandalone: true, selector: "player-volume-control", ngImport: i0, template: `<media-mute-button class="e11-video-control-button">
      <svg slot="icon" viewBox="0 0 36 36">
        <g id="icon-volume">
          <path id="speaker" d="M13 15H9V21H13L18 26V10L13 15Z" />
          <path id="volume-low" d="M20 22.0323C21.4818 21.2959 22.5 19.7669 22.5 18C22.5 16.2332 21.4818 14.7041 20 13.9678V22.0323Z" />
          <path
            id="volume-high"
            d="M20 9.22302V11.2899C22.8915 12.1505 25 14.829 25 18C25 21.171 22.8915 23.8495 20 24.7101V26.777C24.008 25.8675 27 22.2832 27 18C27 13.7168 24.008 10.1325 20 9.22302Z"
          />
        </g>
        <g id="icon-muted">
          <path
            d="M10.2207 8.80817L8.80762 10.2213L13.2929 14.7065L13 14.9995H9V20.9995H13L18 25.9995V19.4136L22.1922 23.6058C21.5401 24.0942 20.8 24.4715 20 24.7096V26.7764C21.3453 26.4712 22.5761 25.8646 23.6177 25.0314L25.7782 27.1918L27.1924 25.7776L27.1913 25.7766L27.1902 25.7776L10.2207 8.80817Z"
          />
          <path
            d="M25.8817 22.3478C26.5944 21.0589 27 19.5766 27 17.9995C27 13.7163 24.008 10.132 20 9.22247V11.2894C22.8915 12.1499 25 14.8284 25 17.9995C25 19.0177 24.7826 19.9851 24.3917 20.8578L25.8817 22.3478Z"
          />
          <path d="M22.4139 18.88C22.4704 18.5952 22.5 18.3008 22.5 17.9995C22.5 16.2326 21.4818 14.7036 20 13.9672V16.4661L22.4139 18.88Z" />
          <path d="M18 14.4661V9.99945L15.7667 12.2328L18 14.4661Z" />
        </g>
      </svg>
    </media-mute-button>
    <media-volume-range></media-volume-range>`, isInline: true, styles: ["media-mute-button :is(#icon-muted,#icon-volume){transition:clip-path .3s ease-out}media-mute-button #icon-muted{clip-path:inset(0 0 100% 0)}media-mute-button[mediavolumelevel=off] #icon-muted{clip-path:inset(0)}media-mute-button #icon-volume{clip-path:inset(0)}media-mute-button[mediavolumelevel=off] #icon-volume{clip-path:inset(100% 0 0 0)}media-mute-button #volume-high,media-mute-button[mediavolumelevel=off] #volume-high{opacity:1;transition:opacity .3s}media-mute-button[mediavolumelevel=low] #volume-high,media-mute-button[mediavolumelevel=medium] #volume-high{opacity:.2}media-volume-range{height:36px;--media-range-track-background: rgba(255, 255, 255, .2)}media-mute-button+media-volume-range{width:0;overflow:hidden;transition:width .2s ease-in}media-mute-button:hover+media-volume-range,media-mute-button:focus+media-volume-range,media-mute-button:focus-within+media-volume-range,media-volume-range:hover,media-volume-range:focus,media-volume-range:focus-within{width:70px}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerVolumeControlComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-volume-control', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<media-mute-button class="e11-video-control-button">
      <svg slot="icon" viewBox="0 0 36 36">
        <g id="icon-volume">
          <path id="speaker" d="M13 15H9V21H13L18 26V10L13 15Z" />
          <path id="volume-low" d="M20 22.0323C21.4818 21.2959 22.5 19.7669 22.5 18C22.5 16.2332 21.4818 14.7041 20 13.9678V22.0323Z" />
          <path
            id="volume-high"
            d="M20 9.22302V11.2899C22.8915 12.1505 25 14.829 25 18C25 21.171 22.8915 23.8495 20 24.7101V26.777C24.008 25.8675 27 22.2832 27 18C27 13.7168 24.008 10.1325 20 9.22302Z"
          />
        </g>
        <g id="icon-muted">
          <path
            d="M10.2207 8.80817L8.80762 10.2213L13.2929 14.7065L13 14.9995H9V20.9995H13L18 25.9995V19.4136L22.1922 23.6058C21.5401 24.0942 20.8 24.4715 20 24.7096V26.7764C21.3453 26.4712 22.5761 25.8646 23.6177 25.0314L25.7782 27.1918L27.1924 25.7776L27.1913 25.7766L27.1902 25.7776L10.2207 8.80817Z"
          />
          <path
            d="M25.8817 22.3478C26.5944 21.0589 27 19.5766 27 17.9995C27 13.7163 24.008 10.132 20 9.22247V11.2894C22.8915 12.1499 25 14.8284 25 17.9995C25 19.0177 24.7826 19.9851 24.3917 20.8578L25.8817 22.3478Z"
          />
          <path d="M22.4139 18.88C22.4704 18.5952 22.5 18.3008 22.5 17.9995C22.5 16.2326 21.4818 14.7036 20 13.9672V16.4661L22.4139 18.88Z" />
          <path d="M18 14.4661V9.99945L15.7667 12.2328L18 14.4661Z" />
        </g>
      </svg>
    </media-mute-button>
    <media-volume-range></media-volume-range>`, styles: ["media-mute-button :is(#icon-muted,#icon-volume){transition:clip-path .3s ease-out}media-mute-button #icon-muted{clip-path:inset(0 0 100% 0)}media-mute-button[mediavolumelevel=off] #icon-muted{clip-path:inset(0)}media-mute-button #icon-volume{clip-path:inset(0)}media-mute-button[mediavolumelevel=off] #icon-volume{clip-path:inset(100% 0 0 0)}media-mute-button #volume-high,media-mute-button[mediavolumelevel=off] #volume-high{opacity:1;transition:opacity .3s}media-mute-button[mediavolumelevel=low] #volume-high,media-mute-button[mediavolumelevel=medium] #volume-high{opacity:.2}media-volume-range{height:36px;--media-range-track-background: rgba(255, 255, 255, .2)}media-mute-button+media-volume-range{width:0;overflow:hidden;transition:width .2s ease-in}media-mute-button:hover+media-volume-range,media-mute-button:focus+media-volume-range,media-mute-button:focus-within+media-volume-range,media-volume-range:hover,media-volume-range:focus,media-volume-range:focus-within{width:70px}\n"] }]
        }] });

class PlayerControlBarComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerControlBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: PlayerControlBarComponent, isStandalone: true, selector: "player-control-bar", inputs: { config: "config" }, ngImport: i0, template: `
    <media-control-bar>
      <player-play-button></player-play-button>
      <player-volume-control></player-volume-control>
      <player-time-display></player-time-display>
      <!-- Control Spacer -->
      <span class="control-spacer"></span>
      <player-caption-button class="e11-hidden sm:e11-block"></player-caption-button>
      <player-settings-menu-button></player-settings-menu-button>
      @if (config.enablePictureOnPicture) {
      <player-mini-player-button></player-mini-player-button>
      }
      <player-full-screen-button></player-full-screen-button>
    </media-control-bar>
  `, isInline: true, styles: [".control-spacer{flex-grow:1}media-control-bar{position:absolute;height:36px;line-height:36px;bottom:0;left:12px;right:12px}[breakpointmd] media-control-bar{height:48px;line-height:48px}[mediaisfullscreen] media-control-bar{height:54px;line-height:54px}\n"], dependencies: [{ kind: "component", type: PlayerPlayButtonComponent, selector: "player-play-button" }, { kind: "component", type: PlayerVolumeControlComponent, selector: "player-volume-control" }, { kind: "component", type: PlayerTimeDisplayComponent, selector: "player-time-display" }, { kind: "component", type: PlayerCaptionButtonComponent, selector: "player-caption-button" }, { kind: "component", type: PlayerSettingsMenuButtonComponent, selector: "player-settings-menu-button" }, { kind: "component", type: PlayerMiniPlayerButtonComponent, selector: "player-mini-player-button" }, { kind: "component", type: PlayerFullScreenButtonComponent, selector: "player-full-screen-button" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerControlBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-control-bar', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [
                        PlayerPlayButtonComponent,
                        PlayerVolumeControlComponent,
                        PlayerTimeDisplayComponent,
                        PlayerCaptionButtonComponent,
                        PlayerSettingsMenuButtonComponent,
                        PlayerMiniPlayerButtonComponent,
                        PlayerFullScreenButtonComponent,
                    ], encapsulation: ViewEncapsulation.None, template: `
    <media-control-bar>
      <player-play-button></player-play-button>
      <player-volume-control></player-volume-control>
      <player-time-display></player-time-display>
      <!-- Control Spacer -->
      <span class="control-spacer"></span>
      <player-caption-button class="e11-hidden sm:e11-block"></player-caption-button>
      <player-settings-menu-button></player-settings-menu-button>
      @if (config.enablePictureOnPicture) {
      <player-mini-player-button></player-mini-player-button>
      }
      <player-full-screen-button></player-full-screen-button>
    </media-control-bar>
  `, styles: [".control-spacer{flex-grow:1}media-control-bar{position:absolute;height:36px;line-height:36px;bottom:0;left:12px;right:12px}[breakpointmd] media-control-bar{height:48px;line-height:48px}[mediaisfullscreen] media-control-bar{height:54px;line-height:54px}\n"] }]
        }], propDecorators: { config: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class PlayerGradientComponent {
    constructor() {
        this.gradientBG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAACqCAYAAABsziWkAAAAAXNSR0IArs4c6QAAAQVJREFUOE9lyNdHBQAAhfHb3nvvuu2997jNe29TJJEkkkgSSSSJJJJEEkkiifRH5jsP56Xz8PM5gcC/xfDEmjhKxEOCSaREEiSbFEqkQppJpzJMJiWyINvkUCIX8kw+JQqg0BRRxaaEEqVQZsopUQGVpooS1VBjglStqaNEPTSYRko0QbNpoUQrtJl2qsN0UqILuk0PJXqhz/RTYgAGzRA1bEYoMQpjZpwSExAyk5SYgmkzQ82aOUqEIWKilJiHBbNIiSVYhhVYhTVYhw3YhC3Yhh3YhT3YhwM4hCM4hhM4hTM4hwu4hCu4hhu4hTu4hwd4hCd4hhd4hTd4hw/4hC/4hh/4/QM2/id28uIEJAAAAABJRU5ErkJggg==';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerGradientComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerGradientComponent, isStandalone: true, selector: "player-gradient", ngImport: i0, template: ` <div
    class="player-gradient-bottom e11-pt-[37px] e11-absolute e11-w-full e11-h-[170px] e11-bottom-0 e11-pointer-events-none e11-bg-bottom e11-bg-repeat-x"
  ></div>`, isInline: true, styles: [".player-gradient-bottom{border-radius:5px;background-image:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAACqCAYAAABsziWkAAAAAXNSR0IArs4c6QAAAQVJREFUOE9lyNdHBQAAhfHb3nvvuu2997jNe29TJJEkkkgSSSSJJJJEEkkiifRH5jsP56Xz8PM5gcC/xfDEmjhKxEOCSaREEiSbFEqkQppJpzJMJiWyINvkUCIX8kw+JQqg0BRRxaaEEqVQZsopUQGVpooS1VBjglStqaNEPTSYRko0QbNpoUQrtJl2qsN0UqILuk0PJXqhz/RTYgAGzRA1bEYoMQpjZpwSExAyk5SYgmkzQ82aOUqEIWKilJiHBbNIiSVYhhVYhTVYhw3YhC3Yhh3YhT3YhwM4hCM4hhM4hTM4hwu4hCu4hhu4hTu4hwd4hCd4hhd4hTd4hw/4hC/4hh/4/QM2/id28uIEJAAAAABJRU5ErkJggg==)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerGradientComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-gradient', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: ` <div
    class="player-gradient-bottom e11-pt-[37px] e11-absolute e11-w-full e11-h-[170px] e11-bottom-0 e11-pointer-events-none e11-bg-bottom e11-bg-repeat-x"
  ></div>`, styles: [".player-gradient-bottom{border-radius:5px;background-image:url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAACqCAYAAABsziWkAAAAAXNSR0IArs4c6QAAAQVJREFUOE9lyNdHBQAAhfHb3nvvuu2997jNe29TJJEkkkgSSSSJJJJEEkkiifRH5jsP56Xz8PM5gcC/xfDEmjhKxEOCSaREEiSbFEqkQppJpzJMJiWyINvkUCIX8kw+JQqg0BRRxaaEEqVQZsopUQGVpooS1VBjglStqaNEPTSYRko0QbNpoUQrtJl2qsN0UqILuk0PJXqhz/RTYgAGzRA1bEYoMQpjZpwSExAyk5SYgmkzQ82aOUqEIWKilJiHBbNIiSVYhhVYhTVYhw3YhC3Yhh3YhT3YhwM4hCM4hhM4hTM4hwu4hCu4hhu4hTu4hwd4hCd4hhd4hTd4hw/4hC/4hh/4/QM2/id28uIEJAAAAABJRU5ErkJggg==)}\n"] }]
        }] });

class PlayerSettingsMenuComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerSettingsMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerSettingsMenuComponent, isStandalone: true, selector: "player-settings-menu", ngImport: i0, template: `<media-settings-menu hidden anchor="auto">
    <media-settings-menu-item>
      Playback Speed
      <media-playback-rate-menu slot="submenu" hidden>
        <div slot="title">Playback Speed</div>
      </media-playback-rate-menu>
    </media-settings-menu-item>
    <media-settings-menu-item class="quality-settings">
      Quality
      <media-rendition-menu slot="submenu" hidden>
        <div slot="title">Quality</div>
      </media-rendition-menu>
    </media-settings-menu-item>
    <media-settings-menu-item>
      Subtitles/CC
      <media-captions-menu slot="submenu" hidden>
        <div slot="title">Subtitles/CC</div>
      </media-captions-menu>
    </media-settings-menu-item>
  </media-settings-menu>`, isInline: true, styles: ["media-settings-menu{position:absolute;border-radius:12px;right:12px;bottom:61px;z-index:70;will-change:width,height;text-shadow:0 0 2px rgba(0,0,0,.5);transition:opacity .1s cubic-bezier(0,0,.2,1);-webkit-user-select:none;user-select:none;--media-settings-menu-min-width: 220px}[mediaisfullscreen] media-settings-menu{--media-settings-menu-min-width: 320px;right:24px;bottom:70px}media-settings-menu-item{height:40px;font-size:13px;font-weight:500;padding-top:0;padding-bottom:0}[mediaisfullscreen] media-settings-menu-item{font-size:20px;height:50px}media-settings-menu-item[submenusize=\"0\"]{display:none}.quality-settings[submenusize=\"1\"]{display:none}media-settings-menu-button svg{transition:transform .1s cubic-bezier(.4,0,1,1);transform:rotate(0)}media-settings-menu-button[aria-expanded=true] svg{transform:rotate(30deg)}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerSettingsMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-settings-menu', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: `<media-settings-menu hidden anchor="auto">
    <media-settings-menu-item>
      Playback Speed
      <media-playback-rate-menu slot="submenu" hidden>
        <div slot="title">Playback Speed</div>
      </media-playback-rate-menu>
    </media-settings-menu-item>
    <media-settings-menu-item class="quality-settings">
      Quality
      <media-rendition-menu slot="submenu" hidden>
        <div slot="title">Quality</div>
      </media-rendition-menu>
    </media-settings-menu-item>
    <media-settings-menu-item>
      Subtitles/CC
      <media-captions-menu slot="submenu" hidden>
        <div slot="title">Subtitles/CC</div>
      </media-captions-menu>
    </media-settings-menu-item>
  </media-settings-menu>`, styles: ["media-settings-menu{position:absolute;border-radius:12px;right:12px;bottom:61px;z-index:70;will-change:width,height;text-shadow:0 0 2px rgba(0,0,0,.5);transition:opacity .1s cubic-bezier(0,0,.2,1);-webkit-user-select:none;user-select:none;--media-settings-menu-min-width: 220px}[mediaisfullscreen] media-settings-menu{--media-settings-menu-min-width: 320px;right:24px;bottom:70px}media-settings-menu-item{height:40px;font-size:13px;font-weight:500;padding-top:0;padding-bottom:0}[mediaisfullscreen] media-settings-menu-item{font-size:20px;height:50px}media-settings-menu-item[submenusize=\"0\"]{display:none}.quality-settings[submenusize=\"1\"]{display:none}media-settings-menu-button svg{transition:transform .1s cubic-bezier(.4,0,1,1);transform:rotate(0)}media-settings-menu-button[aria-expanded=true] svg{transform:rotate(30deg)}\n"] }]
        }] });

class PlayerTimeRangeComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerTimeRangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PlayerTimeRangeComponent, isStandalone: true, selector: "player-time-range", ngImport: i0, template: ` <media-time-range>
    <media-preview-thumbnail slot="preview"></media-preview-thumbnail>
    <media-preview-chapter-display slot="preview"></media-preview-chapter-display>
    <media-preview-time-display slot="preview"></media-preview-time-display>
  </media-time-range>`, isInline: true, styles: ["media-time-range{position:absolute;bottom:36px;width:100%;height:5px;--media-range-track-background: rgba(255, 255, 255, .2);--media-range-track-pointer-background: rgba(255, 255, 255, .5);--media-time-range-buffered-color: rgba(255, 255, 255, .4);--media-range-bar-color: var(--media-accent-color, rgb(229, 9, 20));--media-range-thumb-border-radius: 13px;--media-range-thumb-background: var(--media-accent-color, #f00);--media-range-thumb-transition: transform .1s linear;--media-range-thumb-transform: scale(0) translate(0%, 0%)}media-time-range:hover{--media-range-track-height: 5px;--media-range-thumb-transform: scale(1) translate(0%, 0%)}[breakpointmd] media-time-range{bottom:47px}[mediaisfullscreen] media-time-range{bottom:52.5px;height:8px}[mediaisfullscreen] media-time-range:hover{--media-range-track-height: 8px}media-preview-thumbnail{margin-bottom:5px}media-preview-chapter-display{padding-block:0}media-preview-time-display{padding-top:0}\n"], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PlayerTimeRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'player-time-range', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], encapsulation: ViewEncapsulation.None, template: ` <media-time-range>
    <media-preview-thumbnail slot="preview"></media-preview-thumbnail>
    <media-preview-chapter-display slot="preview"></media-preview-chapter-display>
    <media-preview-time-display slot="preview"></media-preview-time-display>
  </media-time-range>`, styles: ["media-time-range{position:absolute;bottom:36px;width:100%;height:5px;--media-range-track-background: rgba(255, 255, 255, .2);--media-range-track-pointer-background: rgba(255, 255, 255, .5);--media-time-range-buffered-color: rgba(255, 255, 255, .4);--media-range-bar-color: var(--media-accent-color, rgb(229, 9, 20));--media-range-thumb-border-radius: 13px;--media-range-thumb-background: var(--media-accent-color, #f00);--media-range-thumb-transition: transform .1s linear;--media-range-thumb-transform: scale(0) translate(0%, 0%)}media-time-range:hover{--media-range-track-height: 5px;--media-range-thumb-transform: scale(1) translate(0%, 0%)}[breakpointmd] media-time-range{bottom:47px}[mediaisfullscreen] media-time-range{bottom:52.5px;height:8px}[mediaisfullscreen] media-time-range:hover{--media-range-track-height: 8px}media-preview-thumbnail{margin-bottom:5px}media-preview-chapter-display{padding-block:0}media-preview-time-display{padding-top:0}\n"] }]
        }] });

class E11MediaControllerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MediaControllerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MediaControllerComponent, isStandalone: true, selector: "e11-media-controller", inputs: { config: "config" }, ngImport: i0, template: ` <media-controller
    [att.breakpoints]="'md:480'"
    [att.defaultsubtitles]="config.defaultSubtitles || ''"
    [att.defaultduration]="config.defaultDuration || '123'"
    [att.gesturesdisabled]="config.disabled ? 'true' : 'false'"
    [att.pictureinpicture]="config.enablePictureOnPicture ? 'true' : 'false'"
    [att.hotkeys]="config.hotkeys || ''"
    [att.nohotkeys]="config.noHotkeys ? 'true' : 'false'"
    [att.defaultstreamtype]="'on-demand'"
    [ngStyle]="config.roundPlayerBorder ? { 'border-radius': '5px' } : null"
    class="e11-grid"
  >
    <!-- <slot name="media" slot="media"></slot> -->
    <!-- <slot name="poster" slot="poster"></slot> -->
    <ng-content name="media"></ng-content>
    <ng-content name="poster"></ng-content>

    <player-gradient></player-gradient>
    <player-settings-menu></player-settings-menu>

    <player-time-range></player-time-range>
    <player-control-bar [config]="config"></player-control-bar>
  </media-controller>`, isInline: true, styles: ["media-controller{font-size:13px;font-family:Roboto,Arial,sans-serif;--media-font-family: Roboto, helvetica neue, segoe ui, arial, sans-serif;-webkit-font-smoothing:antialiased;--media-secondary-color: transparent;--media-menu-background: rgba(28, 28, 28, .9);--media-control-hover-background: var(--media-secondary-color);--media-range-track-height: 3px;--media-range-thumb-height: 13px;--media-range-thumb-width: 13px;--media-range-thumb-border-radius: 13px;--media-preview-thumbnail-border: 2px solid #fff;--media-preview-thumbnail-border-radius: 2px;--media-tooltip-display: none;height:100%;width:100%;aspect-ratio:16/9}.rounded-player{border-radius:5px}media-controller[mediaisfullscreen]{font-size:17px;--media-range-thumb-height: 20px;--media-range-thumb-width: 20px;--media-range-thumb-border-radius: 5px;--media-range-track-height: 4px}\n"], dependencies: [{ kind: "component", type: PlayerControlBarComponent, selector: "player-control-bar", inputs: ["config"] }, { kind: "component", type: PlayerTimeRangeComponent, selector: "player-time-range" }, { kind: "component", type: PlayerGradientComponent, selector: "player-gradient" }, { kind: "component", type: PlayerSettingsMenuComponent, selector: "player-settings-menu" }, { kind: "directive", type: NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MediaControllerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-media-controller', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [PlayerControlBarComponent, PlayerTimeRangeComponent, PlayerGradientComponent, PlayerSettingsMenuComponent, NgStyle], template: ` <media-controller
    [att.breakpoints]="'md:480'"
    [att.defaultsubtitles]="config.defaultSubtitles || ''"
    [att.defaultduration]="config.defaultDuration || '123'"
    [att.gesturesdisabled]="config.disabled ? 'true' : 'false'"
    [att.pictureinpicture]="config.enablePictureOnPicture ? 'true' : 'false'"
    [att.hotkeys]="config.hotkeys || ''"
    [att.nohotkeys]="config.noHotkeys ? 'true' : 'false'"
    [att.defaultstreamtype]="'on-demand'"
    [ngStyle]="config.roundPlayerBorder ? { 'border-radius': '5px' } : null"
    class="e11-grid"
  >
    <!-- <slot name="media" slot="media"></slot> -->
    <!-- <slot name="poster" slot="poster"></slot> -->
    <ng-content name="media"></ng-content>
    <ng-content name="poster"></ng-content>

    <player-gradient></player-gradient>
    <player-settings-menu></player-settings-menu>

    <player-time-range></player-time-range>
    <player-control-bar [config]="config"></player-control-bar>
  </media-controller>`, encapsulation: ViewEncapsulation.None, styles: ["media-controller{font-size:13px;font-family:Roboto,Arial,sans-serif;--media-font-family: Roboto, helvetica neue, segoe ui, arial, sans-serif;-webkit-font-smoothing:antialiased;--media-secondary-color: transparent;--media-menu-background: rgba(28, 28, 28, .9);--media-control-hover-background: var(--media-secondary-color);--media-range-track-height: 3px;--media-range-thumb-height: 13px;--media-range-thumb-width: 13px;--media-range-thumb-border-radius: 13px;--media-preview-thumbnail-border: 2px solid #fff;--media-preview-thumbnail-border-radius: 2px;--media-tooltip-display: none;height:100%;width:100%;aspect-ratio:16/9}.rounded-player{border-radius:5px}media-controller[mediaisfullscreen]{font-size:17px;--media-range-thumb-height: 20px;--media-range-thumb-width: 20px;--media-range-thumb-border-radius: 5px;--media-range-track-height: 4px}\n"] }]
        }], propDecorators: { config: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class E11PlayerPartsComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PlayerPartsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11PlayerPartsComponent, isStandalone: true, selector: "e11-player-parts", inputs: { config: "config" }, ngImport: i0, template: ` <e11-media-controller [config]="config">
    <ng-content></ng-content>
  </e11-media-controller>`, isInline: true, styles: [".e11-video-control-button{position:relative;display:inline-block;width:36px;padding:0 2px;height:100%;opacity:.9;transition:opacity .1s cubic-bezier(.4,0,1,1)}[breakpointmd] .e11-video-control-button{width:48px}[mediaisfullscreen] .e11-video-control-button{width:54px}.e11-video-control-button svg{height:100%;width:100%;fill:var(--media-primary-color, #fff);fill-rule:evenodd}.svg-shadow{stroke:#000;stroke-opacity:.15;stroke-width:2px;fill:none}\n"], dependencies: [{ kind: "component", type: E11MediaControllerComponent, selector: "e11-media-controller", inputs: ["config"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11PlayerPartsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-player-parts', standalone: true, schemas: [], imports: [E11MediaControllerComponent], encapsulation: ViewEncapsulation.None, template: ` <e11-media-controller [config]="config">
    <ng-content></ng-content>
  </e11-media-controller>`, styles: [".e11-video-control-button{position:relative;display:inline-block;width:36px;padding:0 2px;height:100%;opacity:.9;transition:opacity .1s cubic-bezier(.4,0,1,1)}[breakpointmd] .e11-video-control-button{width:48px}[mediaisfullscreen] .e11-video-control-button{width:54px}.e11-video-control-button svg{height:100%;width:100%;fill:var(--media-primary-color, #fff);fill-rule:evenodd}.svg-shadow{stroke:#000;stroke-opacity:.15;stroke-width:2px;fill:none}\n"] }]
        }], propDecorators: { config: [{
                type: Input,
                args: [{ required: true }]
            }] } });

class E11MediaPlayerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MediaPlayerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MediaPlayerComponent, isStandalone: true, selector: "e11-media-player", inputs: { config: "config" }, ngImport: i0, template: `
    <e11-player-parts [config]="config">
      <ng-content></ng-content>
    </e11-player-parts>
  `, isInline: true, dependencies: [{ kind: "component", type: E11PlayerPartsComponent, selector: "e11-player-parts", inputs: ["config"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MediaPlayerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-media-player',
                    standalone: true,
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                    imports: [E11PlayerPartsComponent],
                    encapsulation: ViewEncapsulation.None,
                    template: `
    <e11-player-parts [config]="config">
      <ng-content></ng-content>
    </e11-player-parts>
  `,
                }]
        }], propDecorators: { config: [{
                type: Input,
                args: [{ required: true }]
            }] } });

const isValidUrl = (url) => {
    try {
        return Boolean(new URL(url));
    }
    catch {
        return false;
    }
};
function isCloudflareVideo(src) {
    try {
        return Boolean(new URL(src)) && MATCH_SRC.test(src);
    }
    catch {
        return true;
    }
}
function formatSrcInput(value) {
    if (!value)
        return undefined;
    Object.keys(value).forEach((key) => {
        if (value[key].isCloudflare) {
            value[key].src = isValidUrl(value[key].src)
                ? value[key].src
                : `${EMBED_BASE}/${value[key].src}`;
        }
    });
    return value;
}

class E11VideoPlayerComponent {
    set videoSrcRecord(value) {
        this._videoSrcRecord = value;
        if (!value)
            return;
        this.activeVideoSrc = value.primary;
    }
    constructor(_element) {
        this._element = _element;
        this.config = {
            defaultSubtitles: 'en',
            defaultDuration: '600',
            disabled: false,
            hotkeys: 'space',
            noHotkeys: false,
            enablePictureOnPicture: true,
            roundPlayerBorder: true,
        };
        this.allErrored = false;
        this.loading = true;
        this.abort = new EventEmitter();
        this.canplay = new EventEmitter();
        this.canplaythrough = new EventEmitter();
        this.complete = new EventEmitter();
        this.durationchange = new EventEmitter();
        this.emptied = new EventEmitter();
        this.encrypted = new EventEmitter();
        this.ended = new EventEmitter();
        this.error = new EventEmitter();
        this.loadeddata = new EventEmitter();
        this.loadedmetadata = new EventEmitter();
        this.loadstart = new EventEmitter();
        this.pause = new EventEmitter();
        this.play = new EventEmitter();
        this.playing = new EventEmitter();
        this.progress = new EventEmitter();
        this.ratechange = new EventEmitter();
        this.seeked = new EventEmitter();
        this.seeking = new EventEmitter();
        this.stalled = new EventEmitter();
        this.suspend = new EventEmitter();
        this.timeupdate = new EventEmitter();
        this.volumechange = new EventEmitter();
        this.waiting = new EventEmitter();
        this.waitingforkey = new EventEmitter();
        this.resize = new EventEmitter();
        this.enterpictureinpicture = new EventEmitter();
        this.leavepictureinpicture = new EventEmitter();
    }
    isHlsSource(src) {
        // Check for typical HLS patterns
        return src.includes('.m3u8') || src.includes('hls');
    }
    get getPlayerType() {
        if (this.activeVideoSrc?.isCloudflare) {
            return 'cloudflare';
        }
        else if (this.activeVideoSrc?.src && this.isHlsSource(this.activeVideoSrc.src)) {
            return 'hls';
        }
        else if (this.activeVideoSrc?.src) {
            return 'native';
        }
        return 'none';
    }
    switchToFallback() {
        // check if the error is from fallback
        if (this.activeVideoSrc === this._videoSrcRecord.fallback) {
            this.removeEventListeners(false);
            this.allErrored = true;
            this.stopLoading();
            return;
        }
        this.removeEventListeners(true);
        this.activeVideoSrc = this._videoSrcRecord.fallback;
    }
    stopLoading() {
        this.loading = false;
    }
    bindEventListeners(isCloudflare, evt) {
        this._element.nativeElement.dispatchEvent(new Event(evt.type));
        const player = isCloudflare ? this.cloudflareplayer : this.player;
        for (let type of VideoEvents) {
            player.nativeElement.addEventListener(type, (evt) => {
                this._element.nativeElement.dispatchEvent(new Event(evt.type));
            });
        }
    }
    removeEventListeners(isCloudflare) {
        const player = isCloudflare ? this.cloudflareplayer : this.player;
        for (let type of VideoEvents) {
            player.nativeElement.removeEventListener(type, () => { });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11VideoPlayerComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: E11VideoPlayerComponent, isStandalone: true, selector: "e11-video-player", inputs: { config: "config", thumb: "thumb", videoSrcRecord: ["videoSrcRecord", "videoSrcRecord", formatSrcInput] }, outputs: { abort: "abort", canplay: "canplay", canplaythrough: "canplaythrough", complete: "complete", durationchange: "durationchange", emptied: "emptied", encrypted: "encrypted", ended: "ended", error: "error", loadeddata: "loadeddata", loadedmetadata: "loadedmetadata", loadstart: "loadstart", pause: "pause", play: "play", playing: "playing", progress: "progress", ratechange: "ratechange", seeked: "seeked", seeking: "seeking", stalled: "stalled", suspend: "suspend", timeupdate: "timeupdate", volumechange: "volumechange", waiting: "waiting", waitingforkey: "waitingforkey", resize: "resize", enterpictureinpicture: "enterpictureinpicture", leavepictureinpicture: "leavepictureinpicture" }, viewQueries: [{ propertyName: "player", first: true, predicate: ["player"], descendants: true }, { propertyName: "cloudflareplayer", first: true, predicate: ["cloudflareplayer"], descendants: true }], ngImport: i0, template: `
    <div class="e11-relative">
      <ng-container [ngSwitch]="getPlayerType">
        <ng-container *ngSwitchCase="'cloudflare'">
          <e11-media-player [config]="config">
            <cloudflare-video
              #cloudflareplayer
              class="video-player e11-relative"
              [poster]="thumb"
              slot="media"
              src="{{ activeVideoSrc.src }}"
              crossorigin
              (durationchange)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(true, $event)"
            ></cloudflare-video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'hls'">
          <e11-media-player [config]="config">
            <hls-video
              #player
              [poster]="thumb"
              slot="media"
              class="video-player e11-relative"
              [src]="activeVideoSrc.src"
              playsinline
              crossorigin
              (canplay)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(false, $event)"
            ></hls-video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'native'">
          <e11-media-player [config]="config">
            <<video
              #player
              [poster]="thumb"
              slot="media"
              class="video-player e11-relative"
              [src]="activeVideoSrc.src"
              playsinline
              crossorigin
              (canplay)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(false, $event)"
            ></video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'none'">
          <span class="video-center e11-text-skin-white e11-text-lg">{{ 'Video Not Found' }}</span>
        </ng-container>
      </ng-container>
      @if(loading) {
      <player-video-loader class="video-center"></player-video-loader>
      }
    </div>
  `, isInline: true, styles: [".video-player{max-width:100%;max-height:100vh;object-fit:var(--media-object-fit, contain);object-position:var(--media-object-position, 50% 50%);aspect-ratio:16 / 9}.video-center{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}\n"], dependencies: [{ kind: "component", type: E11MediaPlayerComponent, selector: "e11-media-player", inputs: ["config"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: PlayerVideoLoaderComponent, selector: "player-video-loader" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11VideoPlayerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-video-player', standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [E11MediaPlayerComponent, CommonModule, PlayerVideoLoaderComponent], template: `
    <div class="e11-relative">
      <ng-container [ngSwitch]="getPlayerType">
        <ng-container *ngSwitchCase="'cloudflare'">
          <e11-media-player [config]="config">
            <cloudflare-video
              #cloudflareplayer
              class="video-player e11-relative"
              [poster]="thumb"
              slot="media"
              src="{{ activeVideoSrc.src }}"
              crossorigin
              (durationchange)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(true, $event)"
            ></cloudflare-video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'hls'">
          <e11-media-player [config]="config">
            <hls-video
              #player
              [poster]="thumb"
              slot="media"
              class="video-player e11-relative"
              [src]="activeVideoSrc.src"
              playsinline
              crossorigin
              (canplay)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(false, $event)"
            ></hls-video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'native'">
          <e11-media-player [config]="config">
            <<video
              #player
              [poster]="thumb"
              slot="media"
              class="video-player e11-relative"
              [src]="activeVideoSrc.src"
              playsinline
              crossorigin
              (canplay)="stopLoading()"
              (error)="switchToFallback()"
              (loadstart)="bindEventListeners(false, $event)"
            ></video>
          </e11-media-player>
        </ng-container>

        <ng-container *ngSwitchCase="'none'">
          <span class="video-center e11-text-skin-white e11-text-lg">{{ 'Video Not Found' }}</span>
        </ng-container>
      </ng-container>
      @if(loading) {
      <player-video-loader class="video-center"></player-video-loader>
      }
    </div>
  `, styles: [".video-player{max-width:100%;max-height:100vh;object-fit:var(--media-object-fit, contain);object-position:var(--media-object-position, 50% 50%);aspect-ratio:16 / 9}.video-center{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { player: [{
                type: ViewChild,
                args: ['player']
            }], cloudflareplayer: [{
                type: ViewChild,
                args: ['cloudflareplayer']
            }], config: [{
                type: Input
            }], thumb: [{
                type: Input
            }], videoSrcRecord: [{
                type: Input,
                args: [{
                        transform: formatSrcInput,
                    }]
            }], abort: [{
                type: Output
            }], canplay: [{
                type: Output
            }], canplaythrough: [{
                type: Output
            }], complete: [{
                type: Output
            }], durationchange: [{
                type: Output
            }], emptied: [{
                type: Output
            }], encrypted: [{
                type: Output
            }], ended: [{
                type: Output
            }], error: [{
                type: Output
            }], loadeddata: [{
                type: Output
            }], loadedmetadata: [{
                type: Output
            }], loadstart: [{
                type: Output
            }], pause: [{
                type: Output
            }], play: [{
                type: Output
            }], playing: [{
                type: Output
            }], progress: [{
                type: Output
            }], ratechange: [{
                type: Output
            }], seeked: [{
                type: Output
            }], seeking: [{
                type: Output
            }], stalled: [{
                type: Output
            }], suspend: [{
                type: Output
            }], timeupdate: [{
                type: Output
            }], volumechange: [{
                type: Output
            }], waiting: [{
                type: Output
            }], waitingforkey: [{
                type: Output
            }], resize: [{
                type: Output
            }], enterpictureinpicture: [{
                type: Output
            }], leavepictureinpicture: [{
                type: Output
            }] } });

var VideoTypes;
(function (VideoTypes) {
    VideoTypes["CloudFlare"] = "CloudFlare";
    VideoTypes["GoogleBucket"] = "GoogleBucket";
    VideoTypes["YouTube"] = "YouTube";
})(VideoTypes || (VideoTypes = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { API_GLOBAL, API_URL, E11MediaPlayerComponent, E11VideoPlayerComponent, EMBED_BASE, MATCH_SRC, VideoEvents, VideoTypes, formatSrcInput, isCloudflareVideo, isValidUrl };
